import { z } from 'zod';

/**
 * Field model and coordinate system.
 *
 * ── Why normalized coordinates ───────────────────────────────────────────────
 * Fields are stored as fractions of the page (0..1) rather than pixels or points,
 * so the same record renders correctly at any zoom, on any screen, and at the
 * completion step where the PDF is written at true size. R-4.3.
 *
 * ── Origin ───────────────────────────────────────────────────────────────────
 * Top-left, y increasing downward — matching the DOM and every drag event we
 * handle. PDF's native origin is BOTTOM-left with y increasing upward. That flip
 * happens in exactly one place, `normalizedToPdf`, which is unit-tested. Doing it
 * ad hoc at each call site is how signatures end up a page-height away from where
 * they were placed.
 */

export const CORE_FIELD_TYPES = [
  'signature', 'initials', 'date_signed', 'name', 'text', 'checkbox',
] as const;

export const EXTENDED_FIELD_TYPES = [
  'email', 'title', 'company', 'radio', 'dropdown',
  'attachment', 'note', 'approve', 'decline',
] as const;

export const FIELD_TYPES = [...CORE_FIELD_TYPES, ...EXTENDED_FIELD_TYPES] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export interface FieldTypeSpec {
  type: FieldType;
  label: string;
  /** Default size in PDF points, converted to normalized units at drop time. */
  defaultWidthPt: number;
  defaultHeightPt: number;
  /** Filled by the system at signing time; the signer cannot edit it. */
  autoFilled: boolean;
  /** Whether the signer types into it. */
  signerEntered: boolean;
  hint: string;
}

export const FIELD_SPECS: Record<FieldType, FieldTypeSpec> = {
  signature:   { type: 'signature',   label: 'Signature',   defaultWidthPt: 180, defaultHeightPt: 44, autoFilled: false, signerEntered: false, hint: 'The signer adopts and applies their signature here.' },
  initials:    { type: 'initials',    label: 'Initials',    defaultWidthPt: 56,  defaultHeightPt: 36, autoFilled: false, signerEntered: false, hint: 'Each placement is applied individually — required by Cal. Civ. Code § 1633.3(b)(4).' },
  date_signed: { type: 'date_signed', label: 'Date signed', defaultWidthPt: 110, defaultHeightPt: 26, autoFilled: true,  signerEntered: false, hint: 'Filled automatically at signing. Not editable by the signer.' },
  name:        { type: 'name',        label: 'Name',        defaultWidthPt: 180, defaultHeightPt: 26, autoFilled: true,  signerEntered: false, hint: 'Filled from the recipient record.' },
  text:        { type: 'text',        label: 'Text',        defaultWidthPt: 180, defaultHeightPt: 26, autoFilled: false, signerEntered: true,  hint: 'Free text entered by the signer.' },
  checkbox:    { type: 'checkbox',    label: 'Checkbox',    defaultWidthPt: 18,  defaultHeightPt: 18, autoFilled: false, signerEntered: true,  hint: 'A single tick box.' },

  email:       { type: 'email',       label: 'Email',       defaultWidthPt: 200, defaultHeightPt: 26, autoFilled: true,  signerEntered: false, hint: 'Filled from the recipient record. Read-only by default.' },
  title:       { type: 'title',       label: 'Title',       defaultWidthPt: 160, defaultHeightPt: 26, autoFilled: false, signerEntered: true,  hint: 'Job title, entered by the signer.' },
  company:     { type: 'company',     label: 'Company',     defaultWidthPt: 200, defaultHeightPt: 26, autoFilled: false, signerEntered: true,  hint: 'Company name, entered by the signer.' },
  radio:       { type: 'radio',       label: 'Radio group', defaultWidthPt: 18,  defaultHeightPt: 18, autoFilled: false, signerEntered: true,  hint: 'One choice from a set. Give every button in the set the same group name.' },
  dropdown:    { type: 'dropdown',    label: 'Dropdown',    defaultWidthPt: 160, defaultHeightPt: 26, autoFilled: false, signerEntered: true,  hint: 'A list of options you define.' },
  attachment:  { type: 'attachment',  label: 'Attachment',  defaultWidthPt: 180, defaultHeightPt: 34, autoFilled: false, signerEntered: true,  hint: 'The signer uploads a file — an insurance certificate, for example.' },
  note:        { type: 'note',        label: 'Note',        defaultWidthPt: 220, defaultHeightPt: 34, autoFilled: false, signerEntered: false, hint: 'Instructional text shown during signing. Part of the field layer, not the document.' },
  approve:     { type: 'approve',     label: 'Approve',     defaultWidthPt: 110, defaultHeightPt: 32, autoFilled: false, signerEntered: false, hint: 'Approver flow only. No signature is applied.' },
  decline:     { type: 'decline',     label: 'Decline',     defaultWidthPt: 110, defaultHeightPt: 32, autoFilled: false, signerEntered: false, hint: 'Approver flow only. Halts the envelope.' },
};

/** Text validation kinds. R-4.5. */
export const VALIDATION_KINDS = [
  'any', 'number', 'date', 'email', 'zip', 'ssn', 'currency', 'regex',
] as const;
export type ValidationKind = (typeof VALIDATION_KINDS)[number];

export const VALIDATION_LABELS: Record<ValidationKind, string> = {
  any: 'Any text', number: 'Number', date: 'Date', email: 'Email address',
  zip: 'ZIP code', ssn: 'SSN (masked)', currency: 'Currency', regex: 'Custom pattern',
};

const VALIDATORS: Record<Exclude<ValidationKind, 'any' | 'regex'>, RegExp> = {
  number:   /^-?\d+(\.\d+)?$/,
  date:     /^\d{1,2}\/\d{1,2}\/\d{4}$|^\d{4}-\d{2}-\d{2}$/,
  email:    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
  zip:      /^\d{5}(-\d{4})?$/,
  ssn:      /^\d{3}-?\d{2}-?\d{4}$/,
  currency: /^\$?\d{1,3}(,\d{3})*(\.\d{2})?$|^\$?\d+(\.\d{2})?$/,
};

/**
 * Validates a signer-entered value.
 *
 * Runs on the client for immediate feedback AND again on the server at
 * completion (R-4.8). The client copy is a convenience; the server copy is the
 * control, and they are the same function so they cannot drift.
 */
export function validateValue(
  value: string,
  kind: ValidationKind,
  pattern?: string
): { ok: boolean; message?: string } {
  const v = value.trim();
  if (v === '') return { ok: true };
  if (kind === 'any') return { ok: true };
  if (kind === 'regex') {
    if (!pattern) return { ok: true };
    try {
      return new RegExp(pattern).test(v)
        ? { ok: true }
        : { ok: false, message: 'Does not match the required format.' };
    } catch {
      // A malformed pattern must not block a signer from finishing.
      return { ok: true };
    }
  }
  const re = VALIDATORS[kind];
  return re.test(v)
    ? { ok: true }
    : { ok: false, message: `Enter a valid ${VALIDATION_LABELS[kind].toLowerCase()}.` };
}

/** Distinct, colour-blind-safe hues that stay legible over white paper. */
export const ROLE_COLORS = [
  '#1f5fa9', '#a3441c', '#1c6b3f', '#6b3fa0', '#a3801c', '#0f6d78',
] as const;

export function roleColor(index: number): string {
  return ROLE_COLORS[index % ROLE_COLORS.length]!;
}

export interface PageSize { width: number; height: number }

/** A rectangle in normalized page units. Origin top-left, y downward. */
export interface NormRect { x: number; y: number; width: number; height: number }

/** A rectangle in PDF user space. Origin bottom-left, y upward. */
export interface PdfRect { x: number; y: number; width: number; height: number }

export interface Field extends NormRect {
  id: string;
  documentId: string;
  pageIndex: number;
  type: FieldType;
  roleId: string;
  required: boolean;
  label: string;
  tooltip?: string;
  fontSize: number;
  order: number;
  /** Text fields only. */
  validation?: ValidationKind;
  validationPattern?: string;
  maxLength?: number;
  /** Radio and dropdown. */
  options?: { value: string; label: string }[];
  /** Radio buttons sharing a groupId form one mutually exclusive set. */
  groupId?: string;
  /** Note fields: the text displayed to the signer. */
  noteText?: string;
}

export const fieldSchema = z.object({
  id: z.string().min(1),
  documentId: z.string().min(1),
  pageIndex: z.number().int().min(0),
  type: z.enum(FIELD_TYPES),
  roleId: z.string().min(1, 'Every field must be assigned to a signer'),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  width: z.number().gt(0).max(1),
  height: z.number().gt(0).max(1),
  required: z.boolean(),
  label: z.string().max(120),
  tooltip: z.string().max(300).optional(),
  fontSize: z.number().min(6).max(72),
  order: z.number().int().min(0),
  validation: z.enum(VALIDATION_KINDS).optional(),
  validationPattern: z.string().max(300).optional(),
  maxLength: z.number().int().min(1).max(10000).optional(),
  options: z.array(z.object({ value: z.string().max(120), label: z.string().max(120) })).max(50).optional(),
  groupId: z.string().max(64).optional(),
  noteText: z.string().max(1000).optional(),
});

export const MIN_FIELD_PT = 8;

export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/** Keeps a rect fully inside the page, preserving size where possible. */
export function clampToPage(r: NormRect): NormRect {
  const width = clamp(r.width, 0.001, 1);
  const height = clamp(r.height, 0.001, 1);
  return {
    width, height,
    x: clamp(r.x, 0, 1 - width),
    y: clamp(r.y, 0, 1 - height),
  };
}

export function isFullyOnPage(r: NormRect): boolean {
  return r.x >= 0 && r.y >= 0 && r.x + r.width <= 1 + 1e-9 && r.y + r.height <= 1 + 1e-9;
}

/**
 * Normalized (top-left origin) → PDF user space (bottom-left origin).
 *
 * The ONLY place the y-axis flips. Everything else in the codebase stays in
 * top-left space.
 */
export function normalizedToPdf(r: NormRect, page: PageSize): PdfRect {
  return {
    x: r.x * page.width,
    y: (1 - r.y - r.height) * page.height,
    width: r.width * page.width,
    height: r.height * page.height,
  };
}

/** Inverse of normalizedToPdf. */
export function pdfToNormalized(r: PdfRect, page: PageSize): NormRect {
  return {
    x: r.x / page.width,
    y: 1 - (r.y + r.height) / page.height,
    width: r.width / page.width,
    height: r.height / page.height,
  };
}

/** Normalized → rendered pixels, for absolute positioning over the canvas. */
export function normalizedToScreen(r: NormRect, renderedW: number, renderedH: number) {
  return {
    left: r.x * renderedW,
    top: r.y * renderedH,
    width: r.width * renderedW,
    height: r.height * renderedH,
  };
}

/** Rendered pixels → normalized. Used on every drag and resize. */
export function screenToNormalized(
  px: { left: number; top: number; width: number; height: number },
  renderedW: number,
  renderedH: number
): NormRect {
  return {
    x: px.left / renderedW,
    y: px.top / renderedH,
    width: px.width / renderedW,
    height: px.height / renderedH,
  };
}

/** Default size for a newly dropped field, in normalized units for this page. */
export function defaultSizeFor(type: FieldType, page: PageSize): { width: number; height: number } {
  const spec = FIELD_SPECS[type];
  const wPt = spec?.defaultWidthPt ?? 150;
  const hPt = spec?.defaultHeightPt ?? 26;
  return { width: clamp(wPt / page.width, 0.001, 1), height: clamp(hPt / page.height, 0.001, 1) };
}

/**
 * True when two rects genuinely overlap.
 *
 * The epsilon is not decoration. Binary floating point makes 0.1 + 0.2 equal
 * 0.30000000000000004, so two fields aligned flush edge-to-edge — which is what
 * the align tools produce, constantly — would otherwise register as overlapping
 * and raise a warning on every tidy layout. At 1e-6 of a Letter page the
 * tolerance is 0.0006 pt, far below anything visible or meaningful.
 */
const OVERLAP_EPSILON = 1e-6;

export function overlaps(a: NormRect, b: NormRect): boolean {
  return a.x + OVERLAP_EPSILON < b.x + b.width
      && b.x + OVERLAP_EPSILON < a.x + a.width
      && a.y + OVERLAP_EPSILON < b.y + b.height
      && b.y + OVERLAP_EPSILON < a.y + a.height;
}
